'use client';
import { useEffect, useRef, useState } from 'react';
import { Bell, Check, CheckCheck, Info, OctagonAlert, TriangleAlert, X, type LucideIcon } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useAlerts } from '../../lib/queries';
import { useDismissAlert, useMarkAlertRead, useMarkAllAlertsRead } from '../../lib/mutations';
import { formatTaskTime } from '../../lib/format';
import type { Alert, AlertSeverity } from '../../lib/types';
import { EmptyState, ErrorState, LoadingState, Spinner } from '../ui/states';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/shadcn/popover';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Avatar } from '../ui/Avatar';
import { ScrollArea } from '../ui/shadcn/scroll-area';
import { ShellLink } from './ShellLink';

/** How a severity paints: the glyph and its tone colour, shared by every row. `info` takes the accent
 *  rather than a neutral — it is still something a producer chose to raise, not silence. */
const SEVERITY_PRESENTATION: Record<AlertSeverity, { icon: LucideIcon; text: string }> = {
  critical: { icon: OctagonAlert, text: 'text-destructive' },
  warning: { icon: TriangleAlert, text: 'text-warning' },
  info: { icon: Info, text: 'text-primary' },
};
/** Panel order: critical first, then warning, then info. Array#sort is stable, so rows the daemon
 *  already ordered by newest movement keep that order inside one severity. */
const SEVERITY_ORDER: readonly AlertSeverity[] = ['critical', 'warning', 'info'];

/** The badge stops counting visually where it would outgrow the button; the accessible name still
 *  carries the true number, and the panel is one click away. */
const BADGE_MAX = 99;
const SWIPE_REVEAL_THRESHOLD = 32;

/** The instance bell: one icon button in the top bar with the unread count riding on it, opening the
 *  in-place alerts panel. The listing and the badge are ONE query (`GET /alerts` returns both), and the
 *  read-marking mutations write that cache optimistically, so the count can never disagree with the
 *  rows below it. Freshness is push, not poll: every raise/clear publishes a content-free per-recipient
 *  `alert` SSE event that invalidates the query (see lib/useElowenEvents.ts).
 *
 *  Its box is the shared button `icon` step, like every other control in that bar: 36px, 44px on a coarse
 *  pointer. It used to pin 32px in its own class and take a `control` geometry prop from the caller, which
 *  is how one bar row came out 36/32/36. */
export function AlertsBell() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const alerts = useAlerts();
  const unread = alerts.data?.unread ?? 0;
  const { mutate: markAllRead } = useMarkAllAlertsRead();
  const markedOnThisOpen = useRef(false);

  // Opening is the acknowledgement. Wait for an in-flight first load, then attempt exactly once for this
  // opening. A rejected mutation restores the cache and is not retried in a loop; closing and reopening
  // gives the user an intentional retry.
  useEffect(() => {
    if (!open) {
      markedOnThisOpen.current = false;
      return;
    }
    if (!alerts.data || markedOnThisOpen.current) return;
    markedOnThisOpen.current = true;
    if (unread > 0) markAllRead();
  }, [alerts.data, markAllRead, open, unread]);

  // The badge stays aria-hidden; the unread count belongs in this control's accessible name.
  const label = unread > 0 ? `${t.alerts.bellLabel} (${unread})` : t.alerts.bellLabel;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid="top-bar-alerts"
          aria-label={label}
          title={t.alerts.bellLabel}
          className="top-bar__alerts relative text-muted-foreground hover:text-foreground"
        >
          <Bell size={18} strokeWidth={1.5} aria-hidden />
          {unread > 0 ? (
            <Badge
              aria-hidden
              tone="solid-accent"
              data-testid="top-bar-alerts-count"
              className="absolute -right-1 -top-1 h-5 min-w-5 justify-center rounded-full px-1 py-0 tabular-nums"
            >
              {unread > BADGE_MAX ? `${BADGE_MAX}+` : unread}
            </Badge>
          ) : null}
        </Button>
      </PopoverTrigger>
      {/* Deliberately not portaled (see components/ui/shadcn/popover.tsx). The width is the reading
          measure for full alert text, capped by the viewport so a 320 px phone keeps the panel inside the
          screen instead of pushing it off the edge. The height cap sits on the scroller in AlertsPanel. */}
      <PopoverContent
        align="end"
        aria-label={t.alerts.bellLabel}
        className="flex w-[min(--spacing(96),calc(100vw-var(--spacing)*6))] flex-col gap-1 p-3"
      >
        <AlertsPanel now={Date.now()} onNavigate={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

/** The open panel's content. Mounted only while the popover is open, so `now` is read once per open and
 *  the relative time labels never tick while nobody is looking; each label's tooltip carries the absolute
 *  local time for anything older than a day. */
function AlertsPanel({ now, onNavigate }: { now: number; onNavigate: () => void }) {
  const { t, locale } = useTranslation();
  const alerts = useAlerts();
  const markAllRead = useMarkAllAlertsRead();
  const unread = alerts.data?.unread ?? 0;
  const rows = alerts.data
    ? [...alerts.data.alerts].sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity))
    : [];

  return (
    <>
      <div className="flex items-center justify-between gap-2 pb-1">
        <h2 className="text-section-title text-foreground">{t.alerts.panelTitle}</h2>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => markAllRead.mutate()}
          disabled={unread === 0 || markAllRead.isPending}
          aria-label={t.alerts.markAllRead}
          title={t.alerts.markAllRead}
          className="size-7 shrink-0 text-muted-foreground"
        >
          {markAllRead.isPending ? <Spinner size="xs" /> : <CheckCheck size={15} strokeWidth={1.5} aria-hidden />}
        </Button>
      </div>
      {/* The cap belongs on the scroll viewport itself: a max-height on the panel leaves the viewport's
          own height indefinite, so the rows spilled past the panel's edge instead of scrolling. With the
          header this keeps the panel within 24rem, the cap the shared select wears. */}
      <ScrollArea className="min-h-0 flex-1 [&>[data-slot=scroll-area-viewport]]:max-h-80">
        {alerts.isPending ? (
          <LoadingState variant="list" />
        ) : alerts.isError ? (
          <ErrorState message={t.alerts.loadError} onRetry={() => void alerts.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState icon={Bell} title={t.alerts.emptyTitle} description={t.alerts.emptyDescription} />
        ) : (
          <ul className="flex flex-col gap-0.5">
            {rows.map((alert) => (
              <AlertRow key={alert.id} alert={alert} now={now} locale={locale} onNavigate={onNavigate} />
            ))}
          </ul>
        )}
      </ScrollArea>
    </>
  );
}

/** One alert row. Rows with a target are the whole link; read rows recede instead of disappearing, so
 *  a standing condition stays visible until the producer clears it. The dismiss button sits in reserved
 *  space at the row's right edge and fades in on hover, keyboard focus or a left touch swipe: the row
 *  itself never moves, because sliding it would push the start of the title out of the panel — and an
 *  emulated sticky hover on a phone would leave it there. */
function AlertRow({ alert, now, locale, onNavigate }: { alert: Alert; now: number; locale?: string; onNavigate: () => void }) {
  const { t } = useTranslation();
  const markRead = useMarkAlertRead();
  const dismiss = useDismissAlert();
  const [dismissRevealed, setDismissRevealed] = useState(false);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const presentation = SEVERITY_PRESENTATION[alert.severity];
  const Icon = presentation.icon;
  const time = formatTaskTime(alert.updatedAt, now, locale);
  const read = alert.readAt != null;

  const content = (
    <div className="flex min-w-0 items-start gap-2">
      {/* A person's action shows that person, one title line tall; the title already names them, so the
          face stays out of the accessibility tree like the severity glyph it replaces. The glyph sits in a
          box of the same size, so every row's text starts on one column. */}
      {alert.actor ? (
        <span aria-hidden className="flex shrink-0" data-testid={`alert-row-${alert.id}-actor`}>
          <Avatar user={{ ...alert.actor, avatar: alert.actor.avatar ?? undefined }} size={20} />
        </span>
      ) : (
        <span aria-hidden className="flex size-5 shrink-0 items-center justify-center">
          <Icon size={14} strokeWidth={1.5} className={presentation.text} />
        </span>
      )}
      {/* Everything after the icon hangs from the title's left edge, so the eye reads one column. Text is
          shown in full: an alert says what a person has to act on, and a clamped sentence hides exactly
          the number or name that decides what to do. The panel scrolls instead. */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* A long absolute date wraps below the title instead of squeezing it into a column. */}
        <span className="flex min-w-0 flex-wrap items-start gap-x-1.5">
          <span className={`min-w-0 grow basis-24 break-words ${read ? 'text-meta text-muted-foreground' : 'text-card-title text-foreground'}`}>
            {alert.title}
          </span>
          <span className="ml-auto mt-0.5 flex shrink-0 gap-1.5 text-small">
            {time.label ? <span className="text-muted-foreground" title={time.title}>{time.label}</span> : null}
          </span>
        </span>
        {alert.subtitle ? (
          <span className="break-words text-meta text-subtle-foreground" data-testid={`alert-row-${alert.id}-subtitle`}>{alert.subtitle}</span>
        ) : null}
        {alert.body ? (
          <span className="mt-1 break-words text-meta text-muted-foreground">{alert.body}</span>
        ) : null}
        {alert.facts.length > 0 ? (
          // One line per fact rather than a shared grid, so a long value in one row (a model name) cannot
          // narrow every other label. A label keeps at least 4rem and wraps beside its value; a value that
          // cannot fit beside that moves under its label, still flush right.
          <dl className="mt-2 flex flex-col gap-1 border-t border-border pt-2 text-small" data-testid={`alert-row-${alert.id}-facts`}>
            {alert.facts.map(({ label, value }, index) => (
              <div key={index} className="flex min-w-0 flex-wrap items-baseline gap-x-3">
                <dt className="min-w-0 grow basis-16 break-words text-subtle-foreground">{label}</dt>
                <dd className="ml-auto min-w-0 break-words text-right tabular-nums text-foreground">{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
      </div>
    </div>
  );
  const frame = `flex min-w-0 flex-1 flex-col rounded-md p-2 transition-colors hover:bg-accent ${read ? 'opacity-60' : ''}`;
  const finishSwipe = (clientX: number, clientY: number) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (!start) return;
    const deltaX = clientX - start.x;
    const deltaY = clientY - start.y;
    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;
    setDismissRevealed(deltaX < -SWIPE_REVEAL_THRESHOLD);
  };

  return (
    <li className="group relative rounded-md" data-testid={`alert-row-${alert.id}`}>
      {/* `pr-8` is permanent on purpose: the dismiss button fades in over reserved space, so revealing it
          never re-truncates the title and the row keeps its position at every pointer type. */}
      <div
        className="relative z-[var(--z-lift)] flex touch-pan-y items-start gap-1 pr-8"
        data-testid={`alert-row-${alert.id}-content`}
        onPointerDown={(event) => {
          if (event.pointerType === 'mouse') return;
          pointerStart.current = { x: event.clientX, y: event.clientY };
        }}
        onPointerUp={(event) => finishSwipe(event.clientX, event.clientY)}
        onPointerCancel={() => { pointerStart.current = null; }}
      >
        {alert.url ? (
          <ShellLink href={alert.url} onClick={onNavigate} className={`${frame} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}>
            {content}
          </ShellLink>
        ) : (
          <div className={frame}>{content}</div>
        )}
        {!read ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => markRead.mutate(alert.id)}
            disabled={markRead.isPending}
            aria-label={`${t.alerts.markRead}: ${alert.title}`}
            title={t.alerts.markRead}
            className="pointer-coarse:opacity-100 group-hover:opacity-100 mt-1 size-6 shrink-0 text-muted-foreground opacity-0 focus-visible:opacity-100"
          >
            {markRead.isPending ? <Spinner size="xs" /> : <Check size={13} strokeWidth={1.5} aria-hidden />}
          </Button>
        ) : null}
      </div>
      {/* Last in the row on purpose: the popover autofocuses its first tabbable child, and a destructive
          action must not be what Enter hits the moment the panel opens. */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => dismiss.mutate(alert.id)}
        disabled={dismiss.isPending}
        aria-label={`${t.alerts.dismiss}: ${alert.title}`}
        title={t.alerts.dismiss}
        data-revealed={dismissRevealed ? 'true' : undefined}
        className="absolute right-1 top-1 z-[var(--z-sticky)] size-6 text-muted-foreground opacity-0 transition-opacity hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 data-[revealed=true]:opacity-100"
      >
        {dismiss.isPending ? <Spinner size="xs" /> : <X size={14} strokeWidth={1.5} aria-hidden />}
      </Button>
    </li>
  );
}
